/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Buffer } from 'buffer';

import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';

const Params = z.strictObject({ organizationId: ZodTypes.Id });

export const GetOrganization = {
  id: 'organization.get',
  tag: 'Organization',
  summary: 'Get organization',
  description: 'Retrieve a single organization by ID, to which the user has access to',
  method: 'get',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) => `/organization/${params.organizationId}`,
  request: { params: Params },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.strictObject({
            _links: z.strictObject({
              self: z.strictObject({ href: z.string() }),
              thumbnail: z.strictObject({ href: z.string() }).optional(),
            }),
            id: ZodTypes.Id,
            name: ZodTypes.KubernetesIdentifier,
            sequenceId: z.string(),
            description: z.string().min(1).optional(),
            email: z.string().email().optional(),
            companyName: z.string().min(1),
            vatNumber: z.string().min(1).optional(),
            address: z.strictObject({
              id: ZodTypes.Id,
              line1: z.string().min(1),
              line2: z.string().min(1).optional(),
              line3: z.string().min(1).optional(),
              postalCode: z.string().min(1),
              city: z.string().min(1),
              state: z.string().min(1).optional(),
              countryCode: ZodTypes.Country,
            }),
            thumbnail: z
              .union([
                z.strictObject({ id: ZodTypes.Id, seed: z.string().min(1) }),
                z.strictObject({ id: ZodTypes.Id, image: z.instanceof(Buffer) }),
              ])
              .optional(),
            language: ZodTypes.Language,
            verified: z.boolean(),
            role: ZodTypes.RBAC.Organization.Roles,
          }),
        },
      },
    },
  },
} as const satisfies Endpoint;
